import {
  type ConditionalDisplayConfig,
  type ConditionalOperator,
  findConditionRuleProblem,
  pickT,
} from "@repo/shared";
import type { CustomFieldOption, FlowField } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, patch } from "@/lib/api/_client";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import { log } from "@/lib/logger/browser-logger";
import type { FlowPageLayout, PageType, TabsBlockConfig } from "@/lib/types/flow";
import { ConditionalVisibilityFields } from "./conditional-visibility-fields";
import { T } from "./tab-settings-dialog.i18n";

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  options?: CustomFieldOption[] | null;
  isActive: boolean;
};

type TabSettingsDialogProps = {
  open: boolean;
  flowId: string;
  flowLanguage: string;
  layout: FlowPageLayout | null;
  tabId: string | null;
  availableFields: FlowField[];
  clientCustomFields?: ClientCustomField[];
  onClose: (success: boolean) => void;
  pageType?: PageType;
  /** QL-120 — enables the goal-type source on goal/portfolio pages. */
  portfolioCollectionId?: string | null;
};

export function TabSettingsDialog({
  open,
  flowId,
  flowLanguage,
  layout,
  tabId,
  availableFields,
  clientCustomFields = [],
  onClose,
  pageType = "global",
  portfolioCollectionId = null,
}: TabSettingsDialogProps) {
  const t = pickT(T, flowLanguage);

  // Same rule checks as the block dialog: a rule naming a deleted quiz or an
  // unconfigured assessment type is caught on save. Empty while loading, so
  // the check waits for the lists.
  const { quizzes: flowQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tab settings state
  const [tabDescription, setTabDescription] = useState("");

  // QL-217 — the tab's own conditional display
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  const [sourceFieldId, setSourceFieldId] = useState("");
  const [operator, setOperator] = useState<ConditionalOperator>("equals");
  const [conditionValue, setConditionValue] = useState("");

  useEffect(() => {
    const load = (description: string, rule: ConditionalDisplayConfig | undefined) => {
      setTabDescription(description);
      setConditionalEnabled(rule?.enabled || false);
      setSourceFieldId(rule?.sourceFieldId || "");
      setOperator(rule?.operator || "equals");
      setConditionValue(rule?.value || "");
    };

    if (open && layout && tabId) {
      // Parse existing config
      try {
        const config: TabsBlockConfig = layout.config ? JSON.parse(layout.config) : {};
        const tab = config.tabs?.find((t) => t.id === tabId);
        load(tab?.description || "", tab?.conditionalDisplay);
      } catch (err) {
        log.error({ err }, "failed to parse flow tab layout config");
        load("", undefined);
      }
    } else {
      // Reset when closing
      load("", undefined);
    }
    setError(null);
  }, [layout, tabId, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!layout || !tabId) return;

    setLoading(true);
    setError(null);

    let conditionalDisplay: ConditionalDisplayConfig;
    if (conditionalEnabled) {
      const ruleProblem = findConditionRuleProblem(
        { sourceFieldId, operator, value: conditionValue },
        {
          quizzes: quizzesUnknown ? null : flowQuizzes,
          assessmentTypes: assessmentTypesUnknown ? null : configuredAssessmentTypes,
        },
      );
      if (ruleProblem) {
        setError(conditionRuleProblemMessage(ruleProblem, flowLanguage));
        setLoading(false);
        return;
      }
      conditionalDisplay = {
        enabled: true,
        sourceFieldId,
        operator,
        value: operator === "is_empty" || operator === "is_not_empty" ? undefined : conditionValue,
      };
    } else {
      conditionalDisplay = { enabled: false, sourceFieldId: "", operator: "equals" };
    }

    try {
      // Parse existing config to preserve other settings
      let existingConfig: TabsBlockConfig = { tabs: [] };
      try {
        existingConfig = layout.config ? JSON.parse(layout.config) : { tabs: [] };
      } catch {
        existingConfig = { tabs: [] };
      }

      // Update the specific tab's settings
      const updatedTabs = existingConfig.tabs.map((tab) => {
        if (tab.id === tabId) {
          return {
            ...tab,
            description: tabDescription.trim() || undefined,
            conditionalDisplay,
          };
        }
        return tab;
      });

      const newConfig: TabsBlockConfig = {
        ...existingConfig,
        tabs: updatedTabs,
      };

      await patch(`/internal/flows/${flowId}/layouts/${layout.id}`, {
        config: JSON.stringify(newConfig),
      });

      onClose(true);
    } catch (err) {
      log.error({ err }, "failed to update flow tab settings");
      setError(err instanceof ApiError ? err.message : t.failedToUpdate);
    } finally {
      setLoading(false);
    }
  };

  // Get the current tab name for the dialog title
  const tabName = (() => {
    try {
      if (!layout?.config || !tabId) return t.tab;
      const config: TabsBlockConfig = JSON.parse(layout.config);
      const tab = config.tabs?.find((t) => t.id === tabId);
      return tab?.name || t.tab;
    } catch {
      return t.tab;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {tabName} {t.settings}
          </DialogTitle>
          <DialogDescription>{t.configureDescription}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tab-description">{t.descriptionOptional}</Label>
              <Textarea
                id="tab-description"
                value={tabDescription}
                onChange={(e) => setTabDescription(e.target.value)}
                placeholder={t.descriptionPlaceholder}
                rows={3}
                disabled={loading}
              />
            </div>

            <ConditionalVisibilityFields
              enabled={conditionalEnabled}
              onEnabledChange={setConditionalEnabled}
              sourceFieldId={sourceFieldId}
              onSourceFieldIdChange={setSourceFieldId}
              operator={operator}
              onOperatorChange={setOperator}
              value={conditionValue}
              onValueChange={setConditionValue}
              availableFields={availableFields}
              clientCustomFields={clientCustomFields}
              disabled={loading}
              flowLanguage={flowLanguage}
              description={t.showTabCondition}
              pageType={pageType}
              portfolioCollectionId={portfolioCollectionId}
              flowId={flowId}
            />
          </div>

          <DialogFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={loading}>
              {t.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
